import { BackendError, MemoryStore, type DepositBackend, type Member } from "@opulancia/deposit-core";
import { demoAnalysis } from "./views/analysis.js";
import { demoDocument } from "./views/fiche.js";
import { createClient } from "@supabase/supabase-js";
import { SupabaseDepositBackend } from "./supabase-backend.js";
import { MemoryStudioBackend, SupabaseStudioBackend, type StudioBackend } from "./studio/backend.js";

/** How the app reaches its data: Supabase (production) or the in-memory demo (FICTIVE). */
export interface Connection {
  demo: boolean;
  backend: DepositBackend;
  /** The Atelier's projects and images. */
  studio: StudioBackend;
  /** Sign-in choices: e-mail and password, or the demo's fictitious members. */
  demoMembers: Member[];
  signIn(emailOrMember: string, password?: string): Promise<void>;
  signOut(): Promise<void>;
  /** Demo only: the simulated network is back (after a simulated cut). */
  restoreNetwork(): void;
  /** The bytes of a stored deposit file, read with the member's access (short-lived URL, kept nowhere). */
  readObject(storagePath: string): Promise<Uint8Array>;
}

export function connect(): Connection {
  return import.meta.env["VITE_BACKEND"] === "demo" ? demoConnection() : supabaseConnection();
}

function supabaseConnection(): Connection {
  const url = import.meta.env["VITE_SUPABASE_URL"] as string | undefined;
  const key = import.meta.env["VITE_SUPABASE_PUBLISHABLE_KEY"] as string | undefined;
  if (!url || !key) throw new Error("Configuration absente : VITE_SUPABASE_URL et VITE_SUPABASE_PUBLISHABLE_KEY (voir apps/web/.env.example), ou lancer la démonstration : npm run demo");
  const client = createClient(url, key, { auth: { persistSession: true, autoRefreshToken: true } });
  return {
    demo: false,
    backend: new SupabaseDepositBackend(client),
    studio: new SupabaseStudioBackend(client),
    demoMembers: [],
    async signIn(email, password) {
      const { error } = await client.auth.signInWithPassword({ email, password: password ?? "" });
      if (error) throw new Error(/invalid/i.test(error.message) ? "E-mail ou mot de passe incorrect." : error.message);
    },
    async signOut() {
      await client.auth.signOut();
    },
    restoreNetwork() {},
    async readObject(path) {
      const { data, error } = await client.storage.from("originals").createSignedUrl(path, 60);
      if (error || !data) throw new Error(error?.message ?? "Lien de lecture indisponible.");
      const response = await fetch(data.signedUrl, { credentials: "omit", referrerPolicy: "no-referrer" });
      if (!response.ok) throw new Error(`Lecture refusée (${response.status}).`);
      return new Uint8Array(await response.arrayBuffer());
    },
  };
}

/**
 * The demo: the memory backend with two FICTITIOUS members; nothing leaves the
 * browser and everything is lost on reload. `?coupure=N` cuts the simulated
 * network after N files until « Reprendre l'envoi » is pressed, to show an
 * interruption and its resumption.
 */
function demoConnection(): Connection {
  const store = new MemoryStore();
  const members: Member[] = [
    { user_id: "demo-a", display_name: "Membre A (FICTIF)" },
    { user_id: "demo-b", display_name: "Membre B (FICTIF)" },
  ];
  for (const m of members) store.addMember(m.user_id, m.display_name);
  let user: string | null = sessionStorage.getItem("opulancia-demo-user");
  const cutAfter = Number(new URLSearchParams(location.search).get("coupure") ?? NaN);
  let sent = 0;
  let networkDown = false;
  let cutDone = false;
  const urls = new Map<string, string>();
  const analysedOrder = new Map<string, string>();
  const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const backend = new Proxy({} as DepositBackend, {
    get(_target, name: keyof DepositBackend) {
      const real = store.backend(user);
      if (name === "uploadObject") {
        return async (path: string, data: Uint8Array, type: string) => {
          await wait(160);
          if (!cutDone && Number.isFinite(cutAfter) && sent >= cutAfter) networkDown = true;
          if (networkDown) throw new BackendError("network", "Connexion perdue (coupure simulée).");
          sent += 1;
          return real.uploadObject(path, data, type);
        };
      }
      if (name === "getAnalysis") {
        // FICTITIOUS: the demo shows what the section looks like for a saved deposit; as in
        // production, the analysis is no longer current once the slides or their order changed
        return async (id: string) => {
          const detail = await real.getDeposit(id);
          if (detail.deposit.upload_status !== "complete") return null;
          const slides = detail.files.filter((f) => f.role === "slide").sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
          const order = slides.map((f) => f.sha256).join(",");
          if (!analysedOrder.has(id)) analysedOrder.set(id, order);
          return { ...demoAnalysis(slides, detail.deposit.completed_at ?? detail.deposit.created_at), current: analysedOrder.get(id) === order };
        };
      }
      if (name === "getAnalysisDocument") {
        // FICTITIOUS, as getAnalysis: the descriptive sheet of a saved deposit
        return async (id: string) => {
          const detail = await real.getDeposit(id);
          if (detail.deposit.upload_status !== "complete") return null;
          return demoDocument(detail.files.filter((f) => f.role === "slide"), detail.deposit.completed_at ?? detail.deposit.created_at);
        };
      }
      if (name === "getAnalysisJob") return async () => null; // the demo has no job queue
      if (name === "getPublicationStats") {
        // FICTITIOUS statistics for a deposit linked to a publication (none without a link, as in production)
        return async (id: string) => {
          const { deposit } = await real.getDeposit(id);
          if (!deposit.instagram_url) return null;
          const n = deposit.number;
          return {
            matched: true,
            publication_type: "Carrousel",
            published_local: deposit.published_at,
            published_at: null,
            captured_at: deposit.updated_at,
            source: "démo (FICTIF)",
            age_hours: null,
            reach: 1000 * n,
            views: 2400 * n,
            likes: 40 * n,
            comments: 3 * n,
            shares: 11 * n,
            saves: 17 * n,
            profile_visits: null,
            followers_gained: 2 * n,
          };
        };
      }
      if (name === "objectUrl") {
        return async (path: string) => {
          const known = urls.get(path);
          if (known) return known;
          const url = await real.objectUrl(path);
          urls.set(path, url);
          return url;
        };
      }
      const method = real[name] as (...args: unknown[]) => unknown;
      return (...args: unknown[]) => method.apply(real, args);
    },
  });

  const studio = new MemoryStudioBackend(() => user);
  // FICTITIOUS demo only: lets the end-to-end tests act as the other member or cut the network
  (window as unknown as { opulanciaDemo?: unknown }).opulanciaDemo = { studio, setUser: (id: string) => (user = id) };
  return {
    demo: true,
    backend,
    studio,
    demoMembers: members,
    async signIn(member) {
      user = member;
      sessionStorage.setItem("opulancia-demo-user", member);
    },
    async signOut() {
      user = null;
      sessionStorage.removeItem("opulancia-demo-user");
    },
    restoreNetwork() {
      if (networkDown) cutDone = true;
      networkDown = false;
    },
    async readObject(path) {
      await backend.objectUrl(path); // the same access check as a member's URL (cached per path)
      const stored = store.objects.get(path);
      if (!stored) throw new Error("Fichier introuvable.");
      return stored.data.slice();
    },
  };
}
